import { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import exifr from "exifr";
import { api } from "@/lib/api";
import { resizeImageForUpload } from "@/lib/imageResize";
import { Button } from "@/components/ui/button";
import {
  UploadSimple, SpinnerGap, Images, Check, MapPin, CalendarBlank, ArrowLeft, CheckCircle, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Read date + GPS from a file's EXIF, entirely in the browser.
async function readMeta(file) {
  let date = null, lat = null, lon = null;
  try {
    const m = await exifr.parse(file, { pick: ["DateTimeOriginal", "CreateDate"] });
    date = m?.DateTimeOriginal || m?.CreateDate || null;
  } catch { /* no exif */ }
  try { const g = await exifr.gps(file); if (g) { lat = g.latitude; lon = g.longitude; } } catch { /* */ }
  return { date, lat, lon };
}

export default function SelectiveImport() {
  const nav = useNavigate();
  const inputRef = useRef();
  const [step, setStep] = useState("pick"); // pick | scanning | groups | importing | done
  const [items, setItems] = useState([]);   // {id,file,url,date,lat,lon,groupKey}
  const [groups, setGroups] = useState([]);  // {key,label,place,items:[id],count}
  const [selected, setSelected] = useState(new Set());
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [importedCount, setImportedCount] = useState(0);

  const onPick = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setStep("scanning");
    const parsed = [];
    for (let i = 0; i < files.length; i++) {
      const meta = await readMeta(files[i]);
      parsed.push({ id: `${i}-${files[i].name}`, file: files[i], url: URL.createObjectURL(files[i]), ...meta });
    }
    // Group by year-month.
    const gmap = {};
    parsed.forEach((it) => {
      const d = it.date ? new Date(it.date) : null;
      const key = d && !isNaN(d) ? `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}` : "undated";
      it.groupKey = key;
      if (!gmap[key]) gmap[key] = { key, label: key === "undated" ? "No date" : `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
        sortD: d ? d.getTime() : 0, items: [], place: null, firstGps: null };
      gmap[key].items.push(it.id);
      if (!gmap[key].firstGps && it.lat != null) gmap[key].firstGps = { lat: it.lat, lon: it.lon };
    });
    const glist = Object.values(gmap).sort((a, b) => b.sortD - a.sortD);
    setItems(parsed);
    setGroups(glist);
    setSelected(new Set(parsed.map((p) => p.id)));  // everything selected by default
    setStep("groups");
    // Lazily reverse-geocode each group's first GPS for a place label.
    glist.forEach(async (g) => {
      if (!g.firstGps) return;
      try {
        const { data } = await api.get("/geo/reverse", { params: { lat: g.firstGps.lat, lon: g.firstGps.lon } });
        const place = [data.city, data.country].filter(Boolean).join(", ");
        if (place) setGroups((prev) => prev.map((x) => x.key === g.key ? { ...x, place } : x));
      } catch { /* keep no place */ }
    });
  };

  const groupSelected = (g) => g.items.every((id) => selected.has(id));
  const toggleGroup = (g) => setSelected((s) => {
    const n = new Set(s); const all = groupSelected(g);
    g.items.forEach((id) => all ? n.delete(id) : n.add(id)); return n;
  });
  const toggleItem = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const doImport = async () => {
    const chosen = items.filter((it) => selected.has(it.id));
    if (!chosen.length) { toast.error("Select at least one photo"); return; }
    setStep("importing");
    setProgress({ done: 0, total: chosen.length });
    let ok = 0;
    for (let i = 0; i < chosen.length; i++) {
      let toSend = chosen[i].file;
      try { toSend = await resizeImageForUpload(chosen[i].file); } catch { /* original */ }
      const fd = new FormData();
      fd.append("file", toSend);
      fd.append("privacy", "private");
      try { await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch { /* skip failed */ }
      setProgress({ done: i + 1, total: chosen.length });
    }
    setImportedCount(ok);
    setStep("done");
  };

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const selCount = selected.size;

  return (
    <div className="mx-auto max-w-5xl">
      <button onClick={() => nav("/upload")} data-testid="import-back" className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-[#F27A54]">
        <ArrowLeft size={16} weight="bold" /> Upload options
      </button>
      <h1 className="font-display text-3xl font-black tracking-tight">Add from your photos</h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Pick photos from your device — we group them by month &amp; place so you choose exactly which sets to import.
        <span className="font-semibold text-[#2D6A4F]"> Only the photos you select ever leave your device.</span>
      </p>

      {step === "pick" && (
        <div className="mt-8">
          <button data-testid="import-dropzone" onClick={() => inputRef.current?.click()}
            className="flex w-full flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-[#E5DFD3] bg-[#FAF8F5] py-20 transition-colors hover:border-[#F27A54]">
            <UploadSimple size={44} weight="thin" className="text-[#F27A54]" />
            <p className="font-display text-lg font-bold">Choose photos</p>
            <p className="text-sm text-muted-foreground">Select as many as you like — nothing uploads yet</p>
          </button>
          <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif" className="hidden" data-testid="import-file-input"
            onChange={(e) => onPick(e.target.files)} />
        </div>
      )}

      {step === "scanning" && (
        <div className="mt-16 grid place-items-center">
          <SpinnerGap size={34} className="animate-spin text-[#F27A54]" />
          <p className="mt-3 text-sm font-semibold text-muted-foreground">Reading dates &amp; places on your device…</p>
        </div>
      )}

      {step === "groups" && (
        <>
          <div className="sticky top-0 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E5DFD3] bg-white/90 px-4 py-3 backdrop-blur" data-testid="import-toolbar">
            <p className="text-sm font-bold">{items.length} photos · <span className="text-[#F27A54]">{selCount} selected</span></p>
            <div className="flex gap-2">
              <button data-testid="import-select-all" onClick={() => setSelected(new Set(items.map((i) => i.id)))} className="rounded-full border border-[#E5DFD3] px-3 py-1.5 text-xs font-bold hover:border-[#F27A54]">Select all</button>
              <button data-testid="import-select-none" onClick={() => setSelected(new Set())} className="rounded-full border border-[#E5DFD3] px-3 py-1.5 text-xs font-bold hover:border-[#F27A54]">Clear</button>
              <Button data-testid="import-submit" onClick={doImport} disabled={!selCount} className="h-8 gap-1.5 rounded-full bg-[#F27A54] px-4 text-xs font-bold text-white hover:bg-[#d9633f]">
                Import {selCount || ""} <ArrowRight size={13} weight="bold" />
              </Button>
            </div>
          </div>

          <div className="mt-5 space-y-6">
            {groups.map((g) => {
              const gItems = items.filter((it) => it.groupKey === g.key);
              const on = groupSelected(g);
              return (
                <div key={g.key} data-testid={`import-group-${g.key}`}>
                  <button onClick={() => toggleGroup(g)} className="mb-2 flex items-center gap-2" data-testid={`import-group-toggle-${g.key}`}>
                    <span className={`grid h-5 w-5 place-items-center rounded-md border ${on ? "border-[#F27A54] bg-[#F27A54] text-white" : "border-zinc-300"}`}>{on && <Check size={13} weight="bold" />}</span>
                    <CalendarBlank size={16} weight="fill" className="text-[#F27A54]" />
                    <span className="font-display text-base font-bold">{g.label}</span>
                    {g.place && <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground"><MapPin size={12} weight="fill" /> {g.place}</span>}
                    <span className="text-xs text-muted-foreground">· {g.items.length}</span>
                  </button>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {gItems.map((it) => {
                      const sel = selected.has(it.id);
                      return (
                        <button key={it.id} onClick={() => toggleItem(it.id)} data-testid={`import-item-${it.id}`}
                          className={`relative aspect-square overflow-hidden rounded-lg ${sel ? "ring-2 ring-[#F27A54]" : "opacity-60"}`}>
                          <img src={it.url} alt="" className="h-full w-full object-cover" />
                          {sel && <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-[#F27A54] text-white"><Check size={10} weight="bold" /></span>}
                          {it.lat != null && <MapPin size={12} weight="fill" className="absolute bottom-1 left-1 text-white drop-shadow" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {step === "importing" && (
        <div className="mt-16 grid place-items-center px-6">
          <div className="w-full max-w-md text-center">
            <p className="font-display text-xl font-black">Importing your selection…</p>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-zinc-200">
              <div className="h-full rounded-full bg-gradient-to-r from-[#F27A54] to-[#F59E0B] transition-all" style={{ width: `${pct}%` }} data-testid="import-progress-bar" />
            </div>
            <p className="mt-2 text-sm font-semibold text-muted-foreground" data-testid="import-progress-text">{pct}% · {progress.done} of {progress.total} photos</p>
            <p className="mt-1 text-xs text-muted-foreground">You can keep this open in the background.</p>
          </div>
        </div>
      )}

      {step === "done" && (
        <div className="mt-16 grid place-items-center text-center">
          <CheckCircle size={54} weight="fill" className="text-[#2D6A4F]" />
          <p className="mt-3 font-display text-2xl font-black">Imported {importedCount} photos</p>
          <p className="mt-1 text-sm text-muted-foreground">They're in your Library as private drafts. Review, edit and publish the ones you want to share.</p>
          <div className="mt-5 flex gap-3">
            <Button data-testid="import-goto-library" onClick={() => nav("/library")} className="h-11 gap-1.5 rounded-full bg-[#F27A54] px-6 text-white hover:bg-[#d9633f]"><Images size={16} weight="fill" /> Go to Library</Button>
            <Button data-testid="import-more" variant="outline" onClick={() => { setStep("pick"); setItems([]); setGroups([]); setSelected(new Set()); }} className="h-11 rounded-full px-6">Import more</Button>
          </div>
        </div>
      )}
    </div>
  );
}
